import { FC } from "react";
import {
  AccessRequestRecipients,
  AccountSettingsTab,
  EditFeatureModel,
  EnterpriseAccountsStore,
  NationalIdMaskingPolicy,
} from "./enterpriseAccountsDomain";
import { observer } from "mobx-react";
import { useLocaleFormat, useTranslation } from "@/stores/RootStoreContext";
import { Box, Card, Container, Grid, VBox } from "@/components-v2/Box";
import {
  accountFeatureLabels,
  accountFeatureStatusLabels,
  DecisionTypeCount,
  EnterpriseAccount,
  EnterpriseAccountDetails,
  EnterpriseAccountFeatureStatus,
  EnterpriseAccountFeatureType,
  isFailure,
  isOk,
  isPending,
} from "@/common/common";
import { LoadingIndicator } from "@/components-v2/LoadingIndicator";
import { Alert } from "@/components-v2/Alert";
import {
  SharepointSettings,
  SharepointSettingsDialog,
  SharepointSetupDialog,
} from "./sharepointComponents";
import { SideMenu } from "@/components-v2/SideMenu";
import { MRadioButtonGroup, RadioButtonGroup } from "@/components-v2/RadioButton";
import { TextWithTooltip } from "@/components-v2/TextWithTooltip";
import { SsoSettings, SsoSettingsDialog } from "./ssoComponents";
import { Button, LinkButton } from "@/components-v2/Button";
import { DataTable, TableColumn } from "@/components-v2/DataTable";
import { Tag } from "@/components-v2/Tag";
import { Dialog } from "@/components-v2/Dialog";
import { Checkbox, MCheckboxGroup } from "@/components-v2/Checkbox";
import { DataCard } from "@/components-v2/DataCard";
import { TMessage } from "@/i18n/tt";
import { getDecisionName } from "@/dbot/common/commonDomain";
import { identity } from "lodash";
import { AccountRolesSection } from "@/companyRoles/accountRolesComponents";
import { DashboardSettingsSection } from "@/dashboards/dashboardSettingsComponents";

export const Integrations: FC<{ store: EnterpriseAccountsStore }> = observer(({ store }) => {
  const { t } = useTranslation();
  return (
    <VBox container padding={"lg"} gap={"lg"} maxWidth={"lg"}>
      <h3>{t("enterprise-account.integrations")}</h3>
      {store.integrationSettingsModel && <AddOrUpdateIntegrationSettingsDialog store={store} />}
      {isPending(store.sharepointSettingsResult) && <LoadingIndicator />}
      {isFailure(store.sharepointSettingsResult) && (
        <Alert type={"error"} message={t("general-error")} />
      )}
      {isOk(store.sharepointSettingsResult) && <IntegrationSettings store={store} />}
    </VBox>
  );
});

const EmailNotificationSettings: FC<{ store: EnterpriseAccountsStore }> = observer(({ store }) => {
  const { t } = useTranslation();
  return (
    <VBox container padding={"lg"} gap={"lg"} maxWidth={"lg"}>
      <h3>{t("enterprise-account.account-settings.email-heading")}</h3>
      <Card title={t("enterprise-account.account-settings.email-title")}>
        <VBox gap={"lg"}>
          <div>{t("enterprise-account.account-settings.email.description")}</div>
          <VBox>
            <div>{t("enterprise-account.account-settings.email.receive")}</div>
            <RadioButtonGroup
              name={"emailNotification"}
              selected={
                isOk(store.accountResult)
                  ? store.accountResult.data.account.accessRequestRecipients
                  : AccessRequestRecipients.ALL_ADMINS
              }
              options={[AccessRequestRecipients.ALL_ADMINS, AccessRequestRecipients.ACCOUNT_ADMINS]}
              idFn={(item) => item}
              labelFn={(item) => <TextWithTooltip {...store.accessRequestRecipientLabel(item)} />}
              disabledFn={(item) => store.isAccountAdminsDisabled(item)}
              onChange={(_event, item) => store.setNotificationRecipients(item)}
            />
          </VBox>
        </VBox>
      </Card>
    </VBox>
  );
});

const DataProtectionSettings: FC<{ store: EnterpriseAccountsStore }> = observer(({ store }) => {
  const { t } = useTranslation();
  return (
    <VBox container padding={"lg"} gap={"lg"} maxWidth={"lg"}>
      <h3>{t("enterprise-account.account-settings.data-protection")}</h3>
      <Card
        title={t("enterprise-account.account-settings.data-protection.title")}
        subTitle={t("enterprise-account.account-settings.data-protection.subTitle")}
      >
        <VBox gap={"md"} marginTop={"lg"}>
          <div>
            {t("enterprise-account.account-settings.data-protection.ssn-visibility.question")}
          </div>
          <RadioButtonGroup
            name={"nationalIdMaskingPolicy"}
            selected={
              isOk(store.accountResult)
                ? store.accountResult.data.account.nationalIdMaskingPolicy
                : NationalIdMaskingPolicy.FULLY_MASKED
            }
            options={[
              NationalIdMaskingPolicy.FULLY_MASKED,
              NationalIdMaskingPolicy.ACCOUNT_ADMINS,
              NationalIdMaskingPolicy.ALL_ADMINS,
            ]}
            idFn={(item) => item}
            labelFn={(item) => <TextWithTooltip {...store.dataProtectionLabel(item)} />}
            disabledFn={(item) => store.isAccountAdminsDisabled(item)}
            onChange={(_event, item) => store.setNationalIdMaskingPolicy(item)}
          />
        </VBox>
      </Card>
      {store.hasSuperAdminAccess && <TRankDataSharingSettings store={store} />}
    </VBox>
  );
});

const TRankDataSharingSettings: FC<{ store: EnterpriseAccountsStore }> = observer(({ store }) => {
  const { t } = useTranslation();
  const sharing = isOk(store.accountResult)
    ? store.accountResult.data.account.trankDataSharing
    : true;
  return (
    <Card
      title={t("enterprise-account.account-settings.data-protection.trank.title")}
      subTitle={t("enterprise-account.account-settings.data-protection.trank.subTitle")}
    >
      <VBox gap={"md"} marginTop={"lg"}>
        <div>{t("enterprise-account.account-settings.data-protection.trank.question")}</div>
        <RadioButtonGroup
          name={"trankDataSharing"}
          selected={sharing}
          options={[true, false]}
          idFn={(item) => String(item)}
          labelFn={(item) =>
            item
              ? { key: "enterprise-account.account-settings.data-protection.trank.on" }
              : { key: "enterprise-account.account-settings.data-protection.trank.off" }
          }
          descriptionFn={(item) =>
            item
              ? { key: "enterprise-account.account-settings.data-protection.trank.on-description" }
              : { key: "enterprise-account.account-settings.data-protection.trank.off-description" }
          }
          onChange={(_event, item) => store.setTRankDataSharing(item)}
        />
        <Box textSize={"sm"} className={"subtle"}>
          {t("enterprise-account.account-settings.data-protection.trank.note")}
        </Box>
      </VBox>
    </Card>
  );
});

const AccountAdminPermissionsSettings = observer(function AccountAdminPermissionsSettings({
  store,
}: {
  store: EnterpriseAccountsStore;
}) {
  const { t } = useTranslation();
  return (
    <Card title={{ key: "settings" }} subTitle={t("enterprise-account.account-admin-permissions")}>
      <Box marginTop={"lg"}>
        <Checkbox
          name={"accountAdminsCanUpdateSubscriptions"}
          label={{ key: "enterprise-account.account-admin-permissions.update-subscriptions" }}
          checked={
            isOk(store.accountResult) &&
            store.accountResult.data.account.accountAdminsCanUpdateSubscriptions
          }
          onChange={(event) => store.setAccountAdminsCanUpdateSubscriptions(event.target.checked)}
        />
      </Box>
    </Card>
  );
});

const DangerZone = observer(function DangerZone({ store }: { store: EnterpriseAccountsStore }) {
  const { t } = useTranslation();
  return (
    <Card
      title={{ key: "enterprise-account.danger-zone" }}
      subTitle={t("enterprise-account.danger-zone-description")}
    >
      <VBox gap={"md"}>
        <Box marginTop={"lg"}>
          <Button
            variant={"secondary"}
            color={"error"}
            label={{ key: "enterprise-account.remove-all-companies" }}
            onClick={store.openRemoveAllCompaniesDialog}
          />
          <Button
            variant={"secondary"}
            color={"error"}
            label={{
              key: "enterprise-account.delete-account",
              params: {
                name: isOk(store.accountResult) ? store.accountResult.data.account.name : "",
              },
            }}
            onClick={store.openDeleteAccountDialog}
          />
        </Box>
        <Box>
          <Button
            variant={"primary"}
            color={"error"}
            label={t("enterprise-account.purge-account")}
            onClick={store.openPurgeAccountDialog}
          />
        </Box>
      </VBox>
    </Card>
  );
});

export const Settings: FC<{
  store: EnterpriseAccountsStore;
  accountSettingsTab: AccountSettingsTab;
  data: EnterpriseAccountDetails;
}> = observer(({ store, accountSettingsTab, data }) => {
  switch (accountSettingsTab) {
    case "dashboards":
    case "dashboardCards":
      return (
        <DashboardSettingsSection
          accountId={data.account.id}
          companies={data.companyDetails}
          tab={accountSettingsTab === "dashboards" ? "dashboards" : "cards"}
          hasTemplates={store.hasTemplatesFeature}
        />
      );
    case "companyRoles":
      return (
        <AccountRolesSection
          accountId={data.account.id}
          accountName={data.account.name}
          companies={data.companyDetails}
        />
      );
    case "integrations":
      return <Integrations store={store} />;
    case "email":
      return <EmailNotificationSettings store={store} />;
    case "dataProtection":
      return <DataProtectionSettings store={store} />;
    case "features":
      return <FeaturesView store={store} account={data.account} />;
    case "superAdmin":
      return store.hasSuperAdminAccess ? <SuperAdminSettings store={store} /> : null;
  }
});

export const AccountSettingsLeftPanel: FC<{
  store: EnterpriseAccountsStore;
  accountSettingsTab: AccountSettingsTab;
}> = observer(({ store, accountSettingsTab }) => {
  return (
    <VBox marginX marginTop>
      <SideMenu entries={store.accountSettingMenuProps(accountSettingsTab)} />
    </VBox>
  );
});

const AddOrUpdateIntegrationSettingsDialog: FC<{ store: EnterpriseAccountsStore }> = observer(
  ({ store }) => {
    if (store.integrationSettingsModel?.type === "sharepoint") {
      return <SharepointSettingsDialog store={store} />;
    } else if (
      store.integrationSettingsModel?.type === "google" ||
      store.integrationSettingsModel?.type === "microsoft"
    ) {
      return <SsoSettingsDialog store={store} />;
    }
    return null;
  },
);

const IntegrationSettings: FC<{ store: EnterpriseAccountsStore }> = observer(({ store }) => {
  return (
    <>
      <SsoSettings store={store} />
      <SharepointSettings store={store} />
      <SharepointSetupDialog store={store} />
    </>
  );
});

const FeaturesView = observer(function FeaturesView({
  store,
  account,
}: {
  store: EnterpriseAccountsStore;
  account: EnterpriseAccount;
}) {
  const { t } = useTranslation();
  const accountFeatureMap = new Map(account.features.map((feature) => [feature.type, feature]));
  const tableColumns: TableColumn<EnterpriseAccountFeatureType>[] = [
    {
      name: "feature",
      label: { key: "enterprise-account.feature" },
      getValue: (type) => <div>{t(accountFeatureLabels[type])}</div>,
      align: "left",
    },
    {
      name: "status",
      label: { key: "common.status" },
      getValue: (type) => {
        const feature = accountFeatureMap.get(type);
        const status = feature?.status ?? EnterpriseAccountFeatureStatus.DISABLED;
        if (status === EnterpriseAccountFeatureStatus.ENABLED_FOR_SELECTED_COMPANIES) {
          return (
            <Tag
              label={t("enterprise-account.feature.selected-count", {
                count: feature?.selectedCompanyIds.length ?? 0,
              })}
              color={"success"}
            />
          );
        }
        return (
          <Tag
            label={t(accountFeatureStatusLabels[status])}
            color={status === EnterpriseAccountFeatureStatus.DISABLED ? "neutral" : "success"}
          />
        );
      },
      align: "left",
    },
    {
      name: "edit",
      label: "",
      getValue: (type) =>
        store.hasSuperAdminAccess && (
          <LinkButton
            label={{ key: "enterprise-account.edit" }}
            icon={"edit"}
            onClick={() => store.openEditFeatureDialog(type, accountFeatureMap.get(type))}
          />
        ),
    },
  ];
  return (
    <Container marginTop={"lg"} maxWidth={"lg"} gap={"lg"}>
      <VBox gap={"md"}>
        <h3>{t("enterprise-account.features")}</h3>
        <DataTable
          tableColumns={tableColumns}
          tableData={Object.values(EnterpriseAccountFeatureType)}
          getId={identity}
        />
      </VBox>
      {store.editFeatureModel && (
        <EditFeatureDialog store={store} account={account} model={store.editFeatureModel} />
      )}
    </Container>
  );
});

const EditFeatureDialog = observer(function EditFeatureDialog({
  store,
  account,
  model,
}: {
  store: EnterpriseAccountsStore;
  account: EnterpriseAccount;
  model: EditFeatureModel;
}) {
  const { t } = useTranslation();
  return (
    <Dialog
      title={`${t(accountFeatureLabels[model.type])} - ${account.name}`}
      onCancel={() => store.closeEditFeatureDialog()}
      onSubmit={store.submitEditFeatureDialog}
      isSubmitDisabled={model.isSubmitDisabled}
      maxWidth={"sm"}
    >
      <VBox gap={"md"}>
        <VBox gap={"sm"}>
          <Box fontWeight={"bold"}>{t("common.status")}</Box>
          <MRadioButtonGroup model={model.statusModel} horizontal />
        </VBox>
        {model.isPerCompany && (
          <VBox gap={"sm"}>
            <Box>
              <LinkButton label={t("select-all")} onClick={() => model.companies.selectAll()} />
              {" / "}
              <LinkButton label={t("none")} onClick={() => model.companies.clear()} />
            </Box>
            <MCheckboxGroup
              model={model.companies}
              labelFn={(company) => (
                <Box>
                  <div>{company.companyName}</div>
                  <small className={"subtle"}>{company.orgNo}</small>
                </Box>
              )}
            />
          </VBox>
        )}
      </VBox>
    </Dialog>
  );
});

const DecisionTypeList = observer(function DecisionTypeList({
  title,
  total,
  counts,
}: {
  title: TMessage;
  total: number;
  counts: DecisionTypeCount[];
}) {
  const { t } = useTranslation();
  const { formatNumber } = useLocaleFormat();
  const columns: TableColumn<DecisionTypeCount>[] = [
    {
      name: "decisionType",
      label: { key: "enterprise-account.stats.decision-type" },
      align: "left",
      getValue: (row) => t(getDecisionName(row.decisionType)),
    },
    {
      name: "count",
      label: { key: "enterprise-account.stats.count" },
      align: "right",
      getValue: (row) => formatNumber(row.count),
      getSortValue: (row) => row.count,
    },
  ];
  return (
    <Card title={title}>
      <VBox gap={"md"}>
        <Box textSize={"lg"} fontWeight={"bold"}>
          {t("enterprise-account.stats.total-count", { count: total })}
        </Box>
        {counts.length === 0 ? (
          <Box fg={"subtle"}>{t("enterprise-account.stats.no-decisions")}</Box>
        ) : (
          <DataTable
            tableColumns={columns}
            tableData={counts}
            getId={(row) => row.decisionType}
            initialSortBy={"count"}
            initialSortDirection={"desc"}
          />
        )}
      </VBox>
    </Card>
  );
});

const SuperAdminSettings = observer(function SuperAdminSettings({
  store,
}: {
  store: EnterpriseAccountsStore;
}) {
  const { t } = useTranslation();
  if (isPending(store.statsResult)) {
    return (
      <VBox fullheight justifyContent={"center"}>
        <LoadingIndicator />
      </VBox>
    );
  }
  if (!isOk(store.statsResult)) {
    return (
      <VBox container padding={"lg"} maxWidth={"lg"}>
        <Alert message={{ key: "error.fetch.data" }} type={"error"} />
      </VBox>
    );
  }
  const stats = store.statsResult.data;
  return (
    <VBox container padding={"lg"} gap={"lg"} maxWidth={"lg"}>
      <h2>{t("enterprise-account.super-admin-settings")}</h2>
      <AccountAdminPermissionsSettings store={store} />
      <DataCard
        title={{ key: "enterprise-account.stats.shareholder-register-statements" }}
        value={{ type: "number", value: stats.shareholderRegisterStatementCount }}
      />
      <Grid cols={1} colsMd={2} gap={"lg"}>
        <DecisionTypeList
          title={{ key: "enterprise-account.stats.draft" }}
          total={stats.totalDraftDecisions}
          counts={stats.draftDecisionTypeCounts}
        />
        <DecisionTypeList
          title={{ key: "enterprise-account.stats.approved" }}
          total={stats.totalApprovedDecisions}
          counts={stats.approvedDecisionTypeCounts}
        />
      </Grid>
      <DangerZone store={store} />
    </VBox>
  );
});
